import "./orders-chart.scss";

import { styled } from "@mui/material/styles";
import {
	BarLabelProps,
	BarPlot,
	ChartContainer,
	ChartsXAxis,
} from "@mui/x-charts";
import { interpolateObject } from "@mui/x-charts-vendor/d3-interpolate";
import { useAnimate } from "@mui/x-charts/hooks";
import { S2sDashboardPeriod } from "@TXQueries";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import { LoadingDots } from "../loading-dots";
import Stat from "../reusable/stat/stat";
import { StatsDashboardTimeframeProps } from "./interfaces";

export const OrdersChart: React.FC<{
	isLoadingData: boolean;
	data: StatsDashboardTimeframeProps;
	selectedTimeframe: S2sDashboardPeriod;
	setSelectedTimeframe: (timeframe: S2sDashboardPeriod) => any;
}> = ({ isLoadingData, data, selectedTimeframe, setSelectedTimeframe }) => {
	const { t } = useTranslation();

	return (
		<div className="orders-chart-wrapper">
			{isLoadingData && (
				<LoadingDots customClassname="dashboard-loading-dots" />
			)}

			<div className="orders-chart-right-area">
				<div className="orders-chart-title">
					{t("stats-dashboard.sales-chart.title")}
				</div>

				<div className="orders-chart-controls">
					<div
						className={classNames("orders-chart-button", {
							active: selectedTimeframe === "yesterday",
						})}
						onClick={() => setSelectedTimeframe("yesterday")}
					>
						{t("stats-dashboard.sales-chart.timeframes.yesterday")}
					</div>
					<div
						className={classNames("orders-chart-button", {
							active: selectedTimeframe === "last7days",
						})}
						onClick={() => setSelectedTimeframe("last7days")}
					>
						{t("stats-dashboard.sales-chart.timeframes.last7days")}
					</div>
					<div
						className={classNames("orders-chart-button", {
							active: selectedTimeframe === "last30days",
						})}
						onClick={() => setSelectedTimeframe("last30days")}
					>
						{t("stats-dashboard.sales-chart.timeframes.last30days")}
					</div>
					<div
						className={classNames("orders-chart-button", {
							active: selectedTimeframe === "this_month",
						})}
						onClick={() => setSelectedTimeframe("this_month")}
					>
						{t("stats-dashboard.sales-chart.timeframes.monthToDate")}
					</div>
				</div>

				<ChartContainer
					dataset={data.dataset}
					xAxis={[
						{
							scaleType: "band",
							dataKey: "day",
							// labelStyle
						},
					]}
					series={[
						{
							type: "bar",
							id: "base",
							dataKey: "orders",
							color: "#fffc80",
							minBarSize: 48,
						},
					]}
					height={300}
					margin={{ left: 0 }}
				>
					<BarPlot barLabel="value" slots={{ barLabel: BarLabel }} />
					<ChartsXAxis
						axis="x"
						tickPlacement="middle"
						slotProps={{
							axisTickLabel: {
								style: { fill: "grey" },
							},
						}}
					/>
				</ChartContainer>
			</div>

			<div className="orders-chart-left-area">
				<Stat
					label={t("stats-dashboard.orders.quantity")}
					value={data.quantity}
					changeType="no-change"
				/>
				<Stat
					label={t("stats-dashboard.orders.percentage-changes")}
					value={data.percentage + "%"}
					changeType={data.percentage >= 0 ? "increase" : "decrease"}
					showChangeArrow
				/>
			</div>
		</div>
	);
};

const Text = styled("text")(({ theme }) => ({
	...theme?.typography?.body2,
	stroke: "none",
	fill: "white",
	transition: "opacity 0.2s ease-in, fill 0.2s ease-in",
	textAnchor: "middle",
	dominantBaseline: "central",
	pointerEvents: "none",
}));

function BarLabel(props: BarLabelProps) {
	const {
		seriesId,
		dataIndex,
		color,
		isFaded,
		isHighlighted,
		classes,
		xOrigin,
		yOrigin,
		x,
		y,
		width,
		height,
		layout,
		skipAnimation,
		...otherProps
	} = props;

	const animatedProps = useAnimate(
		{ x: x + width / 2, y: y - 8 },
		{
			initialProps: { x: x + width / 2, y: yOrigin },
			createInterpolator: interpolateObject,
			transformProps: (p) => p,
			applyProps: (element: SVGTextElement, p) => {
				element.setAttribute("x", p.x.toString());
				element.setAttribute("y", p.y.toString());
			},
			skip: skipAnimation,
		}
	);

	return (
		<Text {...otherProps} fill={color} textAnchor="middle" {...animatedProps} />
	);
}
